/**
 * GazeZoneClassifier — maps head pose (yaw/pitch) to physical desk zones.
 *
 * During a paper-based exam the camera sits above the monitor looking at
 * the student. The student's head angles tell us roughly WHERE on the
 * physical desk they are looking:
 *
 *                  ┌──────────────────────┐
 *                  │    교실 앞 / 칠판      │  looking_up   (pitch > 5°)
 *                  ├──────┬────────┬──────┤
 *                  │ 왼쪽  │ 답안지  │ 오른쪽│  desk level   (-35° < pitch ≤ 5°)
 *                  │ 영역  │(자기것) │ 영역  │
 *                  ├──────┴────────┴──────┤
 *                  │    책상 아래 / 무릎    │  under_desk   (pitch ≤ -35°)
 *                  └──────────────────────┘
 *                          [학생]
 *
 * Pitch convention (from useFaceLandmarker):
 *   positive = looking UP,  negative = looking DOWN
 *
 * Yaw convention:
 *   positive = turned one way,  negative = the other
 *   |yaw| > threshold = looking to a side
 */

// ── Zone type & metadata ─────────────────────────────────────

export type GazeZone =
  | 'own_paper'
  | 'left_area'
  | 'right_area'
  | 'looking_up'
  | 'under_desk'
  | 'forward';

export interface ZoneMeta {
  label: string;
  labelEn: string;
  color: string;        // CSS color for filled areas
  colorAlpha: string;   // same color at 25% opacity (for overlay fill)
  icon: string;
}

export const ZONE_META: Record<GazeZone, ZoneMeta> = {
  own_paper:  { label: '자기 답안지', labelEn: 'Own Paper',   color: '#22c55e', colorAlpha: 'rgba(34,197,94,0.25)',  icon: '📝' },
  left_area:  { label: '왼쪽 영역',   labelEn: 'Left',        color: '#eab308', colorAlpha: 'rgba(234,179,8,0.25)',  icon: '👈' },
  right_area: { label: '오른쪽 영역',  labelEn: 'Right',       color: '#eab308', colorAlpha: 'rgba(234,179,8,0.25)',  icon: '👉' },
  looking_up: { label: '교실 앞',     labelEn: 'Up (Board)',  color: '#3b82f6', colorAlpha: 'rgba(59,130,246,0.25)', icon: '🔼' },
  under_desk: { label: '책상 아래',   labelEn: 'Under Desk',  color: '#ef4444', colorAlpha: 'rgba(239,68,68,0.25)',  icon: '📱' },
  forward:    { label: '정면',       labelEn: 'Screen',      color: '#06b6d4', colorAlpha: 'rgba(6,182,212,0.25)',  icon: '🖥️' },
};

// ── Thresholds (degrees) ─────────────────────────────────────

/** Pitch above this → looking up at classroom / board */
const PITCH_UP = 5;
/** Pitch below this → looking at own desk area */
const PITCH_DESK = -8;
/** Pitch below this → under-desk / lap (phone zone) */
const PITCH_UNDER = -35;
/** |yaw| above this → looking to neighbor side */
const YAW_SIDE = 12;

// ── Classifier ───────────────────────────────────────────────

/**
 * Classify the current gaze zone from smoothed head pose angles.
 *
 * @param yaw   Head yaw in degrees (from useFaceLandmarker)
 * @param pitch Head pitch in degrees (positive = up, negative = down)
 */
export function classifyGazeZone(yaw: number, pitch: number): GazeZone {
  // 1. Vertical extremes first
  if (pitch > PITCH_UP) return 'looking_up';
  if (pitch < PITCH_UNDER) return 'under_desk';

  // 2. Side glance (within desk-level vertical range)
  if (yaw < -YAW_SIDE) return 'left_area';
  if (yaw > YAW_SIDE) return 'right_area';

  // 3. Looking down enough → own paper
  if (pitch < PITCH_DESK) return 'own_paper';

  // 4. Default: facing forward (screen / straight ahead)
  return 'forward';
}

// ── Dwell tracker ────────────────────────────────────────────

export interface ZoneDwell {
  zone: GazeZone;
  /** Cumulative milliseconds spent in this zone */
  totalMs: number;
  /** Percentage of total tracked time */
  pct: number;
}

export class GazeZoneDwellTracker {
  private zoneTotals = new Map<GazeZone, number>();
  private lastZone: GazeZone | null = null;
  private lastT = 0;
  private totalMs = 0;

  /** Call every tick with the current zone and timestamp. */
  update(zone: GazeZone, now: number = Date.now()) {
    if (this.lastZone != null && this.lastT > 0) {
      const dt = Math.min(500, now - this.lastT); // cap at 500ms to handle pauses
      this.zoneTotals.set(this.lastZone, (this.zoneTotals.get(this.lastZone) ?? 0) + dt);
      this.totalMs += dt;
    }
    this.lastZone = zone;
    this.lastT = now;
  }

  /** Get dwell stats for all observed zones, sorted by totalMs descending. */
  getStats(): ZoneDwell[] {
    if (this.totalMs <= 0) return [];
    const result: ZoneDwell[] = [];
    this.zoneTotals.forEach((ms, zone) => {
      result.push({ zone, totalMs: ms, pct: ms / this.totalMs });
    });
    result.sort((a, b) => b.totalMs - a.totalMs);
    return result;
  }

  reset() {
    this.zoneTotals.clear();
    this.lastZone = null;
    this.lastT = 0;
    this.totalMs = 0;
  }
}
